"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ShieldCheck, CircleCheck as CheckCircle2, Building2, House as Home, Truck, HardHat, FileCheck, ArrowRight, MapPin, Construction, Layers } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const accessContexts = [{
  id: "residential",
  title: "Residential streets",
  subtitle: "Terraces, conversions & mews",
  description: "Careful street-level planning across SW18 residential roads, pavement license coordination with Wandsworth Council, and secure footings that respect neighbouring properties.",
  icon: Home
}, {
  id: "apartments",
  title: "Apartment blocks",
  subtitle: "Multi-storey & estate refurbishment",
  description: "Independent tied scaffolds, ladder access towers, and hoists configured for external facade repair, window replacements, and communal roof renewals.",
  icon: Building2
}, {
  id: "development",
  title: "Ongoing development",
  subtitle: "New builds & site expansion",
  description: "Phased system and tube-and-fitting access packages that keep pace with groundworks, structural frames, and finishing trades without delaying the master program.",
  icon: Construction
}, {
  id: "commercial",
  title: "Commercial property",
  subtitle: "Town centres & mixed-use units",
  description: "High-street pavement gantries, debris netting, and protective pedestrian tunnels engineered so shops and offices stay open during maintenance.",
  icon: Layers
}, {
  id: "constrained",
  title: "Constrained urban access",
  subtitle: "Rear courtyards & tight footprints",
  description: "Bridging beams, cantilevered returns, and lightweight modular components carried through restricted entries where crane or vehicle access is impossible.",
  icon: Truck
}];
const safetySteps = [{
  step: "01",
  title: "Detailed site assessment",
  description: "We survey ground conditions, pedestrian traffic, overhead utilities, and structural tie points before designing any layout.",
  icon: MapPin
}, {
  step: "02",
  title: "Safe access & erection",
  description: "Erected strictly by qualified scaffolders following NASC technical guidance, complete with edge protection, toe boards, and secured ladders.",
  icon: HardHat
}, {
  step: "03",
  title: "Handover inspection & tagging",
  description: "Every structure receives a formal handover certificate and Scafftag sign-off before your team steps onto the working platform.",
  icon: FileCheck
}];
export default function WandsworthAccess() {
  const container = useRef<HTMLElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".reveal-header", {
        y: 30
      });
      gsap.to(".reveal-header", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true
        }
      });
      gsap.set(".context-card", {
        y: 35
      });
      gsap.to(".context-card", {
        opacity: 1,
        y: 0,
        duration: 0.65,
        stagger: 0.1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".context-grid",
          start: "top 85%",
          once: true
        }
      });
      gsap.set(".safety-card", {
        y: 30
      });
      gsap.to(".safety-card", {
        opacity: 1,
        y: 0,
        duration: 0.65,
        stagger: 0.12,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".safety-grid",
          start: "top 85%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".reveal-header", ".context-card", ".safety-card"], {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    if (document.fonts?.ready) document.fonts.ready.then(refresh);
  }, {
    scope: container
  });
  return <section id="wandsworthsafetyandaccess" ref={container} data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="reveal-header max-w-3xl mb-12 md:mb-16" style={{
        opacity: 0
      }}>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Badge variant="outline" className="border-border text-foreground font-medium">
              SW18 Wandsworth Town
            </Badge>
            <span className="text-muted-foreground text-xs">|</span>
            <span className="text-muted-foreground text-xs font-mono uppercase tracking-wider">
              Principal Coverage District
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-default tracking-tight text-foreground">
            Scaffolding access across Wandsworth.
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            SW18 serves as our principal Wandsworth-town district for scaffolding supply, structural design, and Wandsworth Council pavement licensing. Where project logistics require, we also accommodate contracts across surrounding London Borough of Wandsworth districts—including Battersea (SW11), Putney (SW15), and Balham (SW12)—treated as distinct operational zones with individual access and transport parameters.
          </p>
        </div>

        {/* Wandsworth Access Context Cards */}
        <div className="context-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16 md:mb-20">
          {accessContexts.map((context, i) => {
          const Icon = context.icon;
          return <div key={context.id} data-index={i} className="context-card h-full" style={{
            opacity: 0
          }}>
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                  <CardHeader className="space-y-2">
                    <div className="size-11 rounded-lg bg-muted flex items-center justify-center text-primary mb-1">
                      <Icon className="size-5" />
                    </div>
                    <CardTitle className="text-xl font-semibold text-foreground">
                      {context.title}
                    </CardTitle>
                    <CardDescription className="text-xs font-medium text-primary">
                      {context.subtitle}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {context.description}
                    </p>
                  </CardContent>
                </Card>
              </div>;
        })}

          {/* Contact prompt card filling grid balance on larger screens */}
          <div data-index={5} className="context-card h-full" style={{
          opacity: 0
        }}>
            <Card className="h-full border border-primary/20 bg-accent/40 flex flex-col justify-between p-6">
              <div>
                <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4">
                  <ShieldCheck className="size-5" />
                </div>
                <h3 className="text-xl font-semibold font-default text-foreground mb-2">
                  Have a challenging Wandsworth site?
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  We carry out early desktop and on-site access appraisals to resolve pavement permits, parking bays, and structural clearances across SW18 and wider borough sites.
                </p>
              </div>
              <div className="mt-6 pt-4 border-t border-border">
                <Button asChild className="w-full">
                  <Link to="/ContactRequestQuotePage">
                    Request site appraisal
                    <ArrowRight className="size-4 ml-1.5" />
                  </Link>
                </Button>
              </div>
            </Card>
          </div>
        </div>

        {/* Safety & Compliance Section */}
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 lg:p-10 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            
            {/* Left Column: Safety Narrative & Accreditations */}
            <div className="lg:col-span-5 space-y-6">
              <div>
                <Badge variant="secondary" className="mb-3">
                  Safety Protocol
                </Badge>
                <h3 className="text-2xl sm:text-3xl font-bold font-default tracking-tight text-foreground">
                  Disciplined safety from first survey to final strike.
                </h3>
                <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed">
                  BMF Scaffolding operates with an uncompromising approach to site safety. Every installation in Wandsworth is managed in strict alignment with current health and safety legislation.
                </p>
              </div>

              <div className="space-y-3 pt-2">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 rounded-lg bg-primary/10 p-1 text-primary shrink-0">
                    <CheckCircle2 className="size-4" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">NASC Accredited</h4>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Audited adherence to national safety standards and TG20 compliant technical design practices.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="mt-0.5 rounded-lg bg-primary/10 p-1 text-primary shrink-0">
                    <CheckCircle2 className="size-4" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">CHAS Accredited</h4>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Fully vetted health and safety management processes giving main contractors complete compliance assurance.
                    </p>
                  </div>
                </div>
              </div>

              <Separator className="my-6" />

              <div className="flex flex-wrap items-center gap-3">
                <Button asChild variant="outline" size="sm">
                  <Link to="/HealthSafetyAccreditationsPage">
                    View safety credentials
                  </Link>
                </Button>
                <Button asChild variant="ghost" size="sm">
                  <Link to="/ContactRequestQuotePage">
                    Talk to our team
                  </Link>
                </Button>
              </div>
            </div>

            {/* Right Column: 3-Step Safety Workflow */}
            <div className="lg:col-span-7 space-y-4 safety-grid">
              <h4 className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">
                Standard Operational Process
              </h4>
              
              {safetySteps.map((step, i) => {
              const Icon = step.icon;
              return <div key={step.step} data-index={i} className="safety-card rounded-xl border border-border bg-background p-5 shadow-xs" style={{
                opacity: 0
              }}>
                    <div className="flex items-start gap-4">
                      <div className="size-10 rounded-lg bg-muted flex items-center justify-center font-mono font-bold text-foreground shrink-0 text-sm">
                        {step.step}
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Icon className="size-4 text-primary" />
                          <h5 className="font-semibold text-foreground text-sm sm:text-base">
                            {step.title}
                          </h5>
                        </div>
                        <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>;
            })}
            </div>

          </div>
        </div>

        {/* Nearby Service Areas */}
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">
                South West London & Surrey Locations
              </p>
              <p className="text-sm text-foreground font-medium">
                Serving principal SW18 Wandsworth Town alongside adjacent areas:
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <Button asChild variant="outline" size="sm">
                <Link to="#">
                  Clapham
                </Link>
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to="#">
                  Richmond
                </Link>
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to="#">
                  Kingston upon Thames
                </Link>
              </Button>
              <Button asChild variant="ghost" size="sm">
                <Link to="#">
                  View all areas
                  <ArrowRight className="size-3.5 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>;
}